import type { BillingCurrency } from '@contracts/payment/Currency';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import type { FeesFormFields } from '@src/orders/schemas/ReviewOrderFormPermitFields';
import { OrdersClient } from '@src/orders/services/OrdersClient';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useInvoice } from './useInvoice';

export const useReviewOrder = ({ orderId }: { orderId: string }) => {
  const { t } = useTranslation();
  const { navigateToOrderDetails } = useNavigateToOrderDetails();
  const { showToast } = useToast();
  const { refreshOrders } = useRefreshOrders();
  const { refreshOrder } = useRefreshOrder({ id: orderId });

  const {
    modifyInvoice,
    generateInvoice,
    editInvoice,
    deleteInvoice,
    sendInvoiceByEmail,
    invoiceActionStatus,
  } = useInvoice({ orderId });

  const { mutate, isPending: isCompletingOrder } = useMutation({
    mutationFn: (cta: () => Promise<void>) => cta(),
    onSuccess: () => {
      refreshOrders();
      refreshOrder();
      void navigateToOrderDetails({ orderId });
      showToast({
        severity: ToastLevel.success,
        message: t('editOrder.success'),
      });
    },
    onError: () =>
      showToast({
        severity: ToastLevel.error,
        message: t('editOrder.error'),
      }),
  });

  const reviewOrder = ({
    orderId,
    formValues,
  }: {
    orderId: string;
    formValues: FeesFormFields;
  }) => mutate(() => OrdersClient.review({ orderId, fields: formValues }));

  const completeOrder = ({
    orderId,
    formValues,
    billTo,
    billingCurrency,
  }: {
    orderId: string;
    formValues: FeesFormFields;
    billTo: string;
    billingCurrency: BillingCurrency;
  }) =>
    mutate(() =>
      OrdersClient.complete({
        orderId,
        fields: formValues,
        billTo,
        billingCurrency,
      }),
    );

  const updateInvoiceManually = () => {
    void navigateToOrderDetails({ orderId });
  };

  return {
    modifyInvoice,
    updateInvoiceManually,
    reviewOrder,
    completeOrder,
    generateInvoice,
    editInvoice,
    deleteInvoice,
    sendInvoiceByEmail,
    invoiceActionStatus,
    isCompletingOrder,
  };
};
